/**
 * Session Utilities
 * Centralized military date-time format generation for chat sessions
 * Format: "Chat DDHHMMMMYY" (e.g., "Chat 261550JUL25")
 */

/**
 * Generate military date-time format chat title
 * @param {Date} date - Optional date, defaults to current date
 * @param {Array} existingSessions - Optional array to avoid conflicts
 * @returns {string} Military format title like "Chat 261550JUL25"
 */
export const generateMilitaryTitle = (date = new Date(), existingSessions = []) => {
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const monthNames = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 
                     'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
  const month = monthNames[date.getMonth()];
  const year = String(date.getFullYear()).slice(-2);
  
  const baseTitle = `Chat ${day}${hours}${minutes}${month}${year}`;
  
  // Check if this title already exists
  const existingTitles = existingSessions.map(s => s.title || '');
  const baseTitleExists = existingTitles.includes(baseTitle);
  
  if (!baseTitleExists) {
    return baseTitle;
  }
  
  // If base title exists, find the next available suffix
  let suffix = 1;
  let newTitle;
  do {
    const suffixStr = String(suffix).padStart(2, '0');
    newTitle = `${baseTitle}-${suffixStr}`;
    suffix++;
  } while (existingTitles.includes(newTitle) && suffix <= 99);
  
  return newTitle;
};

/**
 * Validate if a title matches military format
 * @param {string} title - Title to validate
 * @returns {boolean} True if matches military format
 */
export const isMilitaryFormat = (title) => {
  if (!title || typeof title !== 'string') return false;
  
  // Match "Chat DDHHMMMMYY" or "Chat DDHHMMMMYY-NN" (with suffix)
  const militaryPattern = /^Chat \d{2}\d{4}[A-Z]{3}\d{2}(-\d{2})?$/;
  return militaryPattern.test(title);
};

/**
 * Generate fallback military title from session timestamp
 * @param {Object} session - Session object with timestamp
 * @returns {string} Military format title
 */
export const generateFallbackMilitaryTitle = (session) => {
  if (!session) return 'Chat Unknown';
  
  // Try to use session timestamps to recreate military format
  const timestamp = session.lastActivity || session.timestamp || session.created_at;
  
  if (timestamp) {
    try {
      const date = new Date(timestamp);
      if (!isNaN(date.getTime())) {
        return generateMilitaryTitle(date);
      }
    } catch (error) {
      console.warn('⚠️ Failed to parse session timestamp for military title:', error);
    }
  }
  
  // Final fallback with current time but marked as recovered
  const fallbackTitle = generateMilitaryTitle();
  console.warn('⚠️ Using current time for fallback military title:', {
    sessionId: session.sessionId?.slice(-8),
    fallbackTitle,
    originalTimestamp: timestamp
  });
  
  return fallbackTitle;
};

/**
 * Get session title with military format enforcement and custom title support.
 * @param {Object} session - Session object
 * @returns {string|{type: string, title: string}} Military format title string,
 *   custom title object {type:'custom', title}, or untitled fallback
 *   {type:'untitled', title:'Chat session'}
 */
export const getMilitarySessionTitle = (session) => {
  if (!session) return 'Chat Unknown';

  // If we have a non-empty title that is not the legacy placeholder:
  if (session.title && session.title !== 'New Chat Session') {
    // If it matches military format, return the string directly
    if (isMilitaryFormat(session.title)) {
      return session.title;
    }
    // Otherwise it is an AI-generated or custom title - return as custom object
    return { type: 'custom', title: session.title };
  }

  // No title set - return untitled fallback with muted display
  return { type: 'untitled', title: 'Chat session' };
};